import { ChatStatusEnum } from '@/entities/feedbacks'
import { type ChatListModel } from '@/shared/api'
import { useContainerHeight } from '@/shared/lib/react-hooks'
import { Avatar, Flex, List, Spin, Tag, Typography } from 'antd'
import clsx from 'clsx'
import VirtualList from 'rc-virtual-list'

import { getTextMessagePreview } from '../../lib/get-text-message-preview'
import { formatRelativeTime } from '../../lib/relative-time'
import styles from './styles.module.scss'

const CONTAINER_HEIGHT = 300

type FeedbackChatListProps = {
  data: ChatListModel[]
  selectedChatId?: string
  loading: boolean
  onClick: (id: string) => void
  hasNextPage: boolean
  onNextPage: () => void
}
export function FeedbackChatList({
  data,
  selectedChatId,
  loading,
  onClick,
  hasNextPage,
  onNextPage,
}: FeedbackChatListProps) {
  const { ref: listContainerRef, containerHeight } =
    useContainerHeight(CONTAINER_HEIGHT)

  const handleScroll = (e: React.UIEvent<HTMLElement>) => {
    if (
      Math.abs(
        e.currentTarget.scrollHeight -
          e.currentTarget.scrollTop -
          containerHeight,
      ) <= 100
    ) {
      onNextPage()
    }
  }

  return (
    <List
      ref={listContainerRef}
      className={styles.listContainer}
      loading={loading && data.length === 0}
    >
      <VirtualList
        data={data}
        height={containerHeight}
        itemKey={(item) => item.id}
        onScroll={handleScroll}
      >
        {(item, index) => (
          <List.Item
            className={clsx(
              styles.listItem,
              selectedChatId === item.id && styles.selectedItem,
            )}
            onClick={() => onClick(item.id)}
          >
            <List.Item.Meta
              avatar={
                <Avatar size={32} src={item.chatPartnerAvatar?.fullCdnUrl} />
              }
              title={
                <Typography.Text ellipsis strong>
                  {item.name}
                </Typography.Text>
              }
              description={
                item.lastMessage ? (
                  <Flex vertical gap={4}>
                    <Typography.Text
                      ellipsis
                      className={styles.listItemDescription}
                    >
                      {getTextMessagePreview(item.lastMessage)}
                    </Typography.Text>
                    <Typography.Text className={styles.listItemDescription}>
                      {formatRelativeTime(item.lastMessage.date)}
                    </Typography.Text>
                  </Flex>
                ) : null
              }
            />
            {item.status === ChatStatusEnum.Closed ? (
              <Tag color="red" className={styles.listItemTag}>
                Закрыто
              </Tag>
            ) : item.newMessageCount ? (
              <Tag color="purple" className={styles.listItemTag}>
                {item.newMessageCount}
              </Tag>
            ) : null}
            {index === data.length - 1 && hasNextPage && (
              <div className={styles.loadingIndicator}>
                {loading ? <Spin size="small" /> : null}
              </div>
            )}
          </List.Item>
        )}
      </VirtualList>
    </List>
  )
}
